import { useEffect, useState } from 'react'
import { Box, Modal, TextField, Button, Checkbox, FormControlLabel } from '@mui/material'
import { brand, fontFamily, headingSx, subtextSx, btnPrimarySx, btnSecondarySx } from './protoTheme'

const ZONE_PRESETS = [360, 270, 180, 90]

const randomDeviceId = () => {
    const chars = '0123456789abcdef'
    let id = ''
    for (let i = 0; i < 24; i++) id += chars[Math.floor(Math.random() * chars.length)]
    return id
}

const emptyData = { deviceId: '', deviceName: 'Pivot 1', zoneWidth: null, zoneWidthCustom: '', currentAngle: '', noDegreesOnPanel: false, dialAngle: 0 }

// Compass dial: fallback way to enter the angle when the control panel has no degree readout.
const AngleDial = ({ value, onChange }) => {
    const size = 160
    const center = size / 2
    const radius = center - 14

    const handlePick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        const x = e.clientX - rect.left - center
        const y = e.clientY - rect.top - center
        let deg = Math.round((Math.atan2(x, -y) * 180) / Math.PI)
        if (deg < 0) deg += 360
        onChange(deg)
    }

    const rad = (value - 90) * (Math.PI / 180)
    const needleX = center + radius * Math.cos(rad)
    const needleY = center + radius * Math.sin(rad)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <svg width={size} height={size} onClick={handlePick} style={{ cursor: 'crosshair' }}>
                <circle cx={center} cy={center} r={radius} fill="#f3f2f2" stroke="#dedede" strokeWidth="1" />
                <text x={center} y={16} textAnchor="middle" fontSize="11" fill={brand.grey}>N</text>
                <text x={size - 10} y={center + 4} textAnchor="middle" fontSize="11" fill={brand.grey}>E</text>
                <text x={center} y={size - 6} textAnchor="middle" fontSize="11" fill={brand.grey}>S</text>
                <text x={10} y={center + 4} textAnchor="middle" fontSize="11" fill={brand.grey}>W</text>
                <line x1={center} y1={center} x2={needleX} y2={needleY} stroke={brand.navy} strokeWidth="3" strokeLinecap="round" />
                <circle cx={center} cy={center} r="4" fill={brand.navy} />
            </svg>
            <span style={{ ...subtextSx }}>Point the needle toward the machine: {value}°</span>
        </Box>
    )
}

const AddDeviceWizard = ({ open, onClose, onFinish, startStep = 1, initialData = {}, title }) => {
    const [step, setStep] = useState(startStep)
    const [data, setData] = useState({ ...emptyData, ...initialData })
    const [scanToast, setScanToast] = useState(false)

    useEffect(() => {
        if (open) {
            setStep(startStep)
            setData({ ...emptyData, ...initialData })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, startStep])

    const zoneWidth = data.zoneWidth === 'custom' ? Number(data.zoneWidthCustom) : data.zoneWidth

    const handleScan = () => {
        setData({ ...data, deviceId: randomDeviceId() })
        setScanToast(true)
        setTimeout(() => setScanToast(false), 2500)
    }

    const canGoStep2 = data.deviceId.trim().length > 0 && data.deviceName.trim().length > 0
    const canGoStep3 = data.zoneWidth != null && (data.zoneWidth !== 'custom' || (Number(data.zoneWidthCustom) > 0 && Number(data.zoneWidthCustom) <= 360))
    const canGoStep4 = String(data.currentAngle).trim().length > 0 && Number(data.currentAngle) >= 0 && Number(data.currentAngle) <= 360

    const [ackChecked, setAckChecked] = useState(false)
    useEffect(() => { if (open) setAckChecked(false) }, [open, startStep])

    const finish = (startNow) => {
        onFinish({
            deviceId: data.deviceId,
            deviceName: data.deviceName,
            zoneWidth,
            currentAngle: Number(data.currentAngle),
            startNow
        })
    }

    const zoneCopy = zoneWidth === 360
        ? 'Let the machine complete a full rotation'
        : `Let the machine travel all ${zoneWidth || '…'}° to the stop and turn around`

    const presetSx = (selected) => selected
        ? { ...btnPrimarySx, minWidth: 64 }
        : { ...btnSecondarySx, minWidth: 64 }

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 460, maxWidth: '92vw', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                maxHeight: '90vh', overflowY: 'auto', fontFamily
            }}>
                <div style={{ ...subtextSx, fontSize: 12, marginBottom: 4 }}>{title || 'Add device'} · step {step} of 4</div>

                {step === 1 && (
                    <Box>
                        <h3 style={headingSx}>Add device</h3>
                        <Button variant="outlined" fullWidth sx={{ ...btnSecondarySx, mt: 2, mb: 1 }} onClick={handleScan}>📷 Scan QR code</Button>
                        {scanToast && <div style={{ fontFamily, fontSize: 12, color: '#1e773c', marginBottom: 8 }}>Scanned: {data.deviceId}</div>}
                        <div style={{ ...subtextSx, fontSize: 12, margin: '8px 0' }}>Or enter manually</div>
                        <TextField
                            label="Device ID" fullWidth variant="standard" sx={{ mb: 2 }}
                            value={data.deviceId} onChange={(e) => setData({ ...data, deviceId: e.target.value })}
                            helperText="24-character ID from the sticker on the case"
                        />
                        <TextField
                            label="Device name" fullWidth variant="standard" sx={{ mb: 2 }}
                            value={data.deviceName} onChange={(e) => setData({ ...data, deviceName: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="contained" sx={btnPrimarySx} disabled={!canGoStep2} onClick={() => setStep(2)}>Next</Button>
                        </Box>
                    </Box>
                )}

                {step === 2 && (
                    <Box>
                        <h3 style={headingSx}>How many degrees does your pivot sweep?</h3>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, mt: 2 }}>
                            {ZONE_PRESETS.map((p) => (
                                <Button
                                    key={p} variant={data.zoneWidth === p ? 'contained' : 'outlined'}
                                    sx={presetSx(data.zoneWidth === p)}
                                    onClick={() => setData({ ...data, zoneWidth: p })}
                                >{p}°</Button>
                            ))}
                            <Button variant={data.zoneWidth === 'custom' ? 'contained' : 'outlined'} sx={presetSx(data.zoneWidth === 'custom')} onClick={() => setData({ ...data, zoneWidth: 'custom' })}>Other</Button>
                        </Box>
                        {data.zoneWidth === 'custom' && (
                            <TextField
                                label="Zone width (°)" type="number" variant="standard" sx={{ mb: 2 }}
                                value={data.zoneWidthCustom} onChange={(e) => setData({ ...data, zoneWidthCustom: e.target.value })}
                            />
                        )}
                        <div style={{ ...subtextSx, fontSize: 12, margin: '12px 0' }}>
                            Not sure? Check the panel when the machine is parked at each stop, and subtract one value from the other.
                        </div>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button sx={{ ...btnSecondarySx, border: 'none' }} onClick={() => setStep(1)}>Back</Button>
                            <Button variant="contained" sx={btnPrimarySx} disabled={!canGoStep3} onClick={() => setStep(3)}>Next</Button>
                        </Box>
                    </Box>
                )}

                {step === 3 && (
                    <Box>
                        <h3 style={headingSx}>Current position</h3>
                        <div style={{ ...subtextSx, marginTop: 4, marginBottom: 16 }}>
                            Check the control panel and enter the angle it shows
                        </div>
                        {!data.noDegreesOnPanel && (
                            <TextField
                                label="Angle from panel" type="number" variant="standard" fullWidth sx={{ mb: 1 }}
                                value={data.currentAngle} onChange={(e) => setData({ ...data, currentAngle: e.target.value })}
                                helperText="Angles in the system match the panel's readout"
                            />
                        )}
                        <FormControlLabel
                            sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontFamily, fontSize: 14 } }}
                            control={<Checkbox checked={data.noDegreesOnPanel} onChange={(e) => setData({ ...data, noDegreesOnPanel: e.target.checked, currentAngle: e.target.checked ? String(data.dialAngle) : data.currentAngle })} sx={{ color: brand.navy, '&.Mui-checked': { color: brand.navy } }} />}
                            label="The panel doesn't show degrees"
                        />
                        {data.noDegreesOnPanel && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                <AngleDial value={data.dialAngle} onChange={(deg) => setData({ ...data, dialAngle: deg, currentAngle: String(deg) })} />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button sx={{ ...btnSecondarySx, border: 'none' }} onClick={() => setStep(2)}>Back</Button>
                            <Button variant="contained" sx={btnPrimarySx} disabled={!canGoStep4} onClick={() => setStep(4)}>Next</Button>
                        </Box>
                    </Box>
                )}

                {step === 4 && (
                    <Box>
                        <h3 style={headingSx}>The first pass is the calibration run</h3>
                        <div style={{ fontFamily, fontSize: 13, marginTop: 12, marginBottom: 12 }}>
                            The machine must complete a full cycle in one continuous pass, without powering off.
                            This is the one condition the system requires to start.
                        </div>
                        <div style={{ fontFamily, fontSize: 13, marginBottom: 12 }}>
                            Duration depends on your pivot's settings.
                        </div>
                        <Box sx={{ fontFamily, border: `1px solid ${brand.navy}22`, bgcolor: 'rgba(1,0,53,0.03)', borderRadius: 1, p: 1.5, mb: 2, fontSize: 13 }}>
                            Tip: start calibration when the machine is sure to run without stopping, for example at the start of a watering cycle
                        </Box>
                        <div style={{ ...subtextSx, marginBottom: 12 }}>{zoneCopy}</div>
                        <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { fontFamily, fontSize: 14 } }}
                            control={<Checkbox checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} sx={{ color: brand.navy, '&.Mui-checked': { color: brand.navy } }} />}
                            label="Understood, the machine will complete a full cycle"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Button sx={{ ...btnSecondarySx, border: 'none' }} onClick={() => setStep(3)}>Back</Button>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" sx={btnSecondarySx} onClick={() => finish(false)}>Start later</Button>
                                <Button variant="contained" sx={btnPrimarySx} disabled={!ackChecked} onClick={() => finish(true)}>Start calibration</Button>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    )
}

export default AddDeviceWizard
